import { cn } from '@brew/ui/cn'
import type { CSSProperties, ReactNode } from 'react'
import {
  GoldenHourSky,
  type SkyPresetName,
} from '../prompt-showcase/golden-hour-sky'

/**
 * How a scene sits on the golden-hour sky.
 * - `inset`: inside two glassy hairline frames (the feature media frame's
 *   16px + 12px insets and 8px inner radius), the product in its own card.
 * - `bare`: straight on the sky.
 */
export type AppVisualChrome = 'inset' | 'bare'

/** #F7F7F5 at 28%: the hairline that reads on every part of the sky. */
const INSET_LINE = 'border border-[#f7f7f547]'

/**
 * The fixed-size stage every app visual is composed on: a live FeralUI sky
 * (`sky`, the prompt panel's shader in one of its presets) behind a scene of
 * REAL app components. Scenes are
 * authored at true product scale inside this box; the home page scales the
 * whole stage to its column (`AppVisual`), the Paper lab renders it at 1:1.
 * A scene may run off the stage's edges (the send dialog's right and
 * bottom): the stage clips without being a scroll container, so focusing a
 * control past an edge never scrolls the scene out of place.
 */
export function AppVisualStage({
  width,
  height,
  chrome,
  sky,
  innerClassName,
  className,
  style,
  children,
  'data-capture': dataCapture,
}: {
  width: number
  height: number
  chrome: AppVisualChrome
  sky: SkyPresetName
  /** Extra classes on the scene's box (e.g. a ground under the product). */
  innerClassName?: string
  className?: string
  style?: CSSProperties
  children: ReactNode
  /** The Paper capture id (lab only). */
  'data-capture'?: string
}) {
  return (
    <div
      data-capture={dataCapture}
      className={cn(
        'relative isolate shrink-0 overflow-clip rounded-3xl',
        className
      )}
      style={{ width, height, ...style }}
    >
      <GoldenHourSky className="-z-10" preset={sky} />
      {chrome === 'bare' ? (
        <div className={cn('absolute inset-0', innerClassName)}>{children}</div>
      ) : (
        <div className="absolute inset-0 flex flex-col p-4">
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col rounded-[16px] p-3',
              INSET_LINE
            )}
          >
            <div
              className={cn(
                'relative min-h-0 flex-1 overflow-hidden rounded-[8px]',
                INSET_LINE,
                innerClassName
              )}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
